import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { usePageTitle } from "../hooks/usePageTitle";
import { auth, projectInvites, type InvitePreview } from "../api";

// Login renders one of three flows depending on the server's registration mode:
//
//  1. `setup`   — fresh deployment, no users in the DB yet. Server printed a
//                 one-time setup token to its logs. We show a setup-only
//                 screen: the user pastes the token + their first admin
//                 email/password, we register, then auto-login. No way to
//                 reach the regular login form until setup is done.
//
//  2. `open`    — anyone can register. Standard sign-in / sign-up toggle.
//
//  3. `locked`  — only existing users can sign in (default after setup
//                 completes). Hide the "Create an account" toggle entirely.
export function Login() {
  const [mode, setMode] = useState<"loading" | "setup" | "login" | "register" | "mfa">(
    "loading",
  );
  usePageTitle(mode === "setup" ? "Setup" : mode === "register" ? "Create Account" : mode === "mfa" ? "Verify Sign In" : "Login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMFACode] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, verifyMFA, register, authenticated } = useAuth();
  const navigate = useNavigate();

  // ?invite=<token> handling. When present:
  //   - we fetch the invite preview and show a banner above the form
  //   - the email field pre-fills with the invite's email + locks
  //   - in locked-mode servers, the register form becomes accessible
  //     (the invite is the registration credential)
  //   - after a successful login/register, we POST /invites/:token/accept
  //     to add the membership, then navigate the user to /
  const [inviteToken] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("invite") || "";
  });
  const [invitePreview, setInvitePreview] = useState<InvitePreview | null>(null);
  const [inviteErr, setInviteErr] = useState<string | null>(null);

  useEffect(() => {
    if (!inviteToken) return;
    projectInvites
      .preview(inviteToken)
      .then((p) => {
        setInvitePreview(p);
        // Lock the email to the invite's address so the user can't
        // try to claim it with a different account.
        setEmail(p.email);
      })
      .catch((e: any) => {
        setInviteErr(e?.message || "Invite invalid or expired");
      });
  }, [inviteToken]);

  // If the user arrives at /login with a valid session already, bounce
  // them straight to the dashboard. Without this, a user who logs in,
  // closes the tab, then reopens it on /login (bookmarks, back button)
  // sits on the login form forever even though their cookie is still
  // valid — auth.me() succeeds in AuthProvider but the Login page
  // itself never re-checks and stays mounted.
  //
  // Special case: if there's a pending ?invite=<token>, we DO want
  // them to land here so we can accept the invite on their behalf
  // before redirecting. The acceptInviteThenGo path below covers that.
  useEffect(() => {
    if (authenticated === true && !inviteToken) {
      console.log("[login] already authenticated on mount → navigate /");
      navigate("/", { replace: true });
    }
  }, [authenticated, navigate, inviteToken]);

  // If we land on /login already authenticated AND we have an invite
  // token, immediately accept it and bounce to the dashboard. This
  // covers the "open the invite link while logged in" path.
  useEffect(() => {
    if (authenticated === true && inviteToken) {
      projectInvites
        .accept(inviteToken)
        .then(() => navigate("/", { replace: true }))
        .catch((e: any) => {
          setInviteErr(e?.message || "Failed to accept invite");
        });
    }
  }, [authenticated, inviteToken, navigate]);

  // Detect server state on mount. We only care about distinguishing
  // setup from non-setup — open vs locked just toggles the register button
  // visibility on the normal login screen.
  const [regMode, setRegMode] = useState<string>("locked");
  useEffect(() => {
    auth
      .status()
      .then((s) => {
        setRegMode(s.reg_mode);
        setMode(s.needs_setup ? "setup" : "login");
      })
      .catch(() => {
        // If status fails for any reason, fall back to the regular login
        // screen — better than blocking the whole UI behind a setup wall
        // when the server is reachable but /auth/status hiccups.
        setMode("login");
      });
  }, []);

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    // Track which await threw. If register succeeded but auto-login
    // didn't (network blip, rate limit, …), the account exists and the
    // setup token is dead server-side — retrying setup will 403. Fall
    // back to the regular login form so the user can sign in with the
    // credentials they just typed instead of being stuck on a setup
    // screen with a misleading "check the token" error.
    let registered = false;
    try {
      await register(email, password, setupToken.trim());
      registered = true;
      await login(email, password);
      navigate("/");
    } catch (err: any) {
      if (registered) {
        setMode("login");
        setSetupToken("");
        setError("Account created. Please sign in with the password you just chose.");
      } else {
        setError(err.message || "Setup failed — check the token and try again");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    console.log("[login] handleSubmit mode=", mode);
    try {
      if (mode === "register") {
        // In locked mode the server requires a valid invite token
        // (via ?invite or X-Invite-Token). When we have one, pass it
        // through the query string of the registration request — the
        // server reads it before validating the rest of the body.
        await register(email, password, undefined, inviteToken || undefined);
      }
      const result = await login(email, password);
      if (result.mfaRequired) {
        setMFACode("");
        setMode("mfa");
        return;
      }
      // If this login came via an invite link, also accept the invite
      // so the project_members row gets written. Best-effort: a
      // failure here lands the user inside the dashboard without
      // the invited project — they can be re-invited.
      if (inviteToken) {
        try { await projectInvites.accept(inviteToken); } catch (e: any) {
          setInviteErr(e?.message || "Logged in, but failed to accept invite");
        }
      }
      console.log("[login] login() resolved, calling navigate('/')");
      navigate("/");
      console.log("[login] navigate('/') called, new url=", window.location.pathname);
    } catch (err: any) {
      console.log("[login] handleSubmit error:", err?.message || err);
      setError(err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleMFAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await verifyMFA(mfaCode);
      if (inviteToken) {
        try { await projectInvites.accept(inviteToken); } catch (e: any) {
          setInviteErr(e?.message || "Signed in, but failed to accept invite");
        }
      }
      navigate("/");
    } catch (err: any) {
      setError(err?.message === "unauthorized" ? "That authentication code is invalid or expired." : err?.message || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  if (mode === "loading") {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <p className="text-text-muted text-sm">Loading…</p>
      </div>
    );
  }

  if (mode === "setup") {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-full max-w-md px-6">
          <div className="text-center mb-10">
            <h1 className="text-text text-3xl font-bold">Apteva</h1>
            <p className="text-text-muted text-base mt-2">First-time setup</p>
          </div>

          <form
            onSubmit={handleSetup}
            className="border border-border rounded-lg p-8 bg-bg-card"
          >
            <p className="text-text-muted text-xs mb-5 leading-relaxed">
              This server has no users yet. Paste the setup token from the
              server logs and create your admin account. The token is
              single-use — once setup completes, this screen disappears
              forever.
            </p>

            <div className="mb-5">
              <label htmlFor="setup-token" className="block text-text-muted text-sm mb-2">
                Setup token
              </label>
              <input
                id="setup-token"
                type="text"
                value={setupToken}
                onChange={(e) => setSetupToken(e.target.value)}
                className="w-full bg-bg-input border border-border rounded-lg px-4 py-3 text-sm text-text font-mono focus:outline-none focus:border-accent"
                placeholder="apt_…"
                autoComplete="off"
                spellCheck={false}
                required
              />
            </div>

            <div className="mb-5">
              <label htmlFor="setup-email" className="block text-text-muted text-sm mb-2">
                Admin email
              </label>
              <input
                id="setup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-bg-input border border-border rounded-lg px-4 py-3 text-base text-text focus:outline-none focus:border-accent"
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="mb-5">
              <label htmlFor="setup-password" className="block text-text-muted text-sm mb-2">
                Password
              </label>
              <input
                id="setup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-bg-input border border-border rounded-lg px-4 py-3 text-base text-text focus:outline-none focus:border-accent"
                placeholder="at least 8 characters"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>

            {error && <div className="text-red text-sm mb-4">{error}</div>}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-accent text-bg font-bold py-3 rounded-lg text-base hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              {busy ? "Creating admin…" : "Create admin & sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (mode === "mfa") {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-full max-w-md px-6">
          <div className="text-center mb-10">
            <h1 className="text-text text-3xl font-bold">Verify sign in</h1>
            <p className="text-text-muted text-base mt-2">
              Enter the code from your authenticator app.
            </p>
          </div>
          <form onSubmit={handleMFAVerify} className="border border-border rounded-lg p-8 bg-bg-card">
            <label className="block text-text-muted text-sm mb-2" htmlFor="mfa-code">
              Authentication or recovery code
            </label>
            <input
              id="mfa-code"
              type="text"
              value={mfaCode}
              onChange={(event) => setMFACode(event.target.value)}
              className="w-full bg-bg-input border border-border rounded-lg px-4 py-3 text-center font-mono text-lg tracking-widest text-text focus:outline-none focus:border-accent"
              placeholder="000000"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              required
              autoFocus
            />
            <p className="mt-2 text-xs text-text-dim">
              You can also enter one of your single-use recovery codes.
            </p>
            {error && <div className="text-red text-sm mt-4">{error}</div>}
            <button
              type="submit"
              disabled={busy || !mfaCode.trim()}
              className="mt-5 w-full bg-accent text-bg font-bold py-3 rounded-lg text-base hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              onClick={() => { setMode("login"); setMFACode(""); setError(""); }}
              className="mt-4 w-full text-sm text-text-muted hover:text-text"
            >
              Back to sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Normal login / register flow.
  const isRegister = mode === "register";
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-10">
          <h1 className="text-text text-3xl font-bold">Apteva</h1>
          <p className="text-text-muted text-base mt-2">
            Autonomous AI agents
          </p>
        </div>

        {/* Invite banner — shows when ?invite=<token> is on the URL.
            Pre-fills the email field (locked to the invite's address)
            and unlocks the register path even in locked-mode servers,
            since the invite itself authorises the new account. */}
        {invitePreview && (
          <div className="mb-5 border border-accent/40 bg-accent/10 rounded-lg px-4 py-3 text-sm">
            <div className="text-accent font-bold">
              You've been invited to <strong>{invitePreview.project_name || "a project"}</strong>
              {invitePreview.inviter_email ? ` by ${invitePreview.inviter_email}` : ""}.
            </div>
            <div className="text-text-muted text-xs mt-1">
              Sign in (or register) with <span className="font-mono">{invitePreview.email}</span> to accept the invite as a <b>{invitePreview.role}</b>.
            </div>
          </div>
        )}
        {inviteErr && (
          <div className="mb-5 border border-red/40 bg-red/10 rounded-lg px-4 py-3 text-sm text-red">
            {inviteErr}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="border border-border rounded-lg p-8 bg-bg-card"
        >
          <div className="mb-5">
            <label htmlFor="login-email" className="block text-text-muted text-sm mb-2">
              Username or email
            </label>
            <input
              id="login-email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-bg-input border border-border rounded-lg px-4 py-3 text-base text-text focus:outline-none focus:border-accent disabled:opacity-70"
              placeholder="username"
              autoComplete="username"
              required
              disabled={!!invitePreview}
            />
          </div>

          <div className="mb-5">
            <label htmlFor="login-password" className="block text-text-muted text-sm mb-2">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-bg-input border border-border rounded-lg px-4 py-3 text-base text-text focus:outline-none focus:border-accent"
              placeholder="••••••••"
              required
              minLength={8}
            />
          </div>

          {error && <div className="text-red text-sm mb-4">{error}</div>}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-accent text-bg font-bold py-3 rounded-lg text-base hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {busy ? "…" : isRegister ? "Register" : "Sign in"}
          </button>

          {/* Only show the register toggle when the server actually accepts
              public registrations. In `locked` mode the button would lead to
              a guaranteed 403 — unless the user has an invite token, which
              IS the authorisation to create an account in locked mode. */}
          {(regMode === "open" || (invitePreview && regMode === "locked")) && (
            <button
              type="button"
              onClick={() => setMode(isRegister ? "login" : "register")}
              className="w-full text-text-muted text-sm mt-4 hover:text-text transition-colors"
            >
              {isRegister ? "Back to sign in" : "Create an account"}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
